import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Star, Send, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StarsDisplay({ rating, size = 16 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "fill-yellow-400 text-yellow-400" : "text-neutral-700"}
        />
      ))}
    </span>
  );
}

function StarsInput({ value, onChange, size = 28 }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="inline-flex items-center gap-1" role="radiogroup" aria-label="Classificação">
      {[1, 2, 3, 4, 5].map((i) => {
        const active = (hover || value) >= i;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
            data-testid={`review-star-${i}`}
            aria-label={`${i} estrelas`}
          >
            <Star
              size={size}
              className={active ? "fill-yellow-400 text-yellow-400" : "text-neutral-700 hover:text-yellow-400/50"}
            />
          </button>
        );
      })}
    </div>
  );
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ nome: "", rating: 0, comentario: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        axios.get(`${API}/reviews/public?limit=8`),
        axios.get(`${API}/reviews/stats`),
      ]);
      setReviews(rRes.data || []);
      setStats(sRes.data || { average: 0, count: 0 });
    } catch (e) {
      console.error("reviews load error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome.trim()) {
      setError("Indica o teu nome.");
      return;
    }
    if (!form.rating) {
      setError("Escolhe quantas estrelas dás ao estúdio.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        nome: form.nome.trim(),
        rating: form.rating,
        comentario: form.comentario.trim() || null,
      });
      setSuccess(true);
      setForm({ nome: "", rating: 0, comentario: "" });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erro a enviar. Tenta novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgRounded = useMemo(() => Math.round(stats.average), [stats.average]);

  return (
    <section
      id="avaliacoes"
      className="px-6 md:px-12 lg:px-24 py-16 md:py-24"
      data-testid="reviews-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-10 bg-yellow-400" />
          <span className="overline">Avaliações · Quem treina recomenda</span>
        </div>
        <h2 className="font-display uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">
          O que dizem <span className="text-yellow-400">os clientes</span>
        </h2>

        {/* Stats banner */}
        <div className="mt-8 mb-12 flex flex-wrap items-center gap-6 md:gap-10 card p-6 md:p-8" data-testid="reviews-stats">
          <div>
            <div className="flex items-end gap-3">
              <span className="font-display text-6xl md:text-7xl text-yellow-400 leading-none">
                {stats.average.toFixed(1)}
              </span>
              <span className="text-neutral-400 text-sm mb-2">/ 5</span>
            </div>
            <div className="mt-2">
              <StarsDisplay rating={avgRounded} size={20} />
            </div>
          </div>
          <div className="border-l border-neutral-800 pl-6 md:pl-10">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Total de avaliações</div>
            <div className="font-display text-3xl md:text-4xl">
              {stats.count}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
              Google Business
            </div>
          </div>
          <div className="flex-1 min-w-[200px] text-right">
            <a
              href="#avaliar"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="reviews-cta-rate"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("avaliar")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              <Star size={16} /> Deixa a tua avaliação
            </a>
          </div>
        </div>

        {/* Comentários */}
        {loading ? (
          <div className="card p-10 text-center text-neutral-500">
            <Loader2 className="animate-spin mx-auto mb-2" /> A carregar avaliações...
          </div>
        ) : reviews.length === 0 ? (
          <div className="card p-10 text-center text-neutral-500" data-testid="reviews-empty">
            Sê o primeiro a deixar uma avaliação.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-testid="reviews-list">
            {reviews.slice(0, 4).map((r) => (
              <div key={r.id} className="card p-6" data-testid={`review-card-${r.id}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <StarsDisplay rating={r.rating} />
                  <span className="text-[11px] uppercase tracking-widest text-neutral-500">
                    {fmtDate(r.created_at)}
                  </span>
                </div>
                {r.comentario ? (
                  <p className="text-sm md:text-base text-neutral-200 leading-relaxed mb-4 whitespace-pre-wrap">
                    “{r.comentario}”
                  </p>
                ) : null}
                <div className="text-xs uppercase tracking-widest text-yellow-400 font-bold">
                  — {r.nome}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Formulário avaliar */}
        <div id="avaliar" className="mt-14 md:mt-20 grid md:grid-cols-5 gap-8 md:gap-12 items-start">
          <div className="md:col-span-2">
            <div className="overline mb-3">Avaliar o estúdio</div>
            <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight">
              Conta-nos a tua <span className="text-yellow-400">experiência</span>
            </h3>
            <p className="mt-4 text-neutral-400 text-sm md:text-base">
              A tua opinião ajuda outras pessoas a conhecer o estúdio. Demora 30 segundos.
            </p>
          </div>

          <div className="md:col-span-3">
            {success ? (
              <div className="card p-7 border-yellow-400/40 bg-yellow-400/5 text-center" data-testid="review-form-success">
                <CheckCircle2 className="text-yellow-400 mx-auto mb-3" size={40} />
                <h4 className="font-display text-2xl uppercase tracking-tight mb-2">Obrigado!</h4>
                <p className="text-sm text-neutral-300">
                  A tua avaliação foi recebida e ficará visível assim que for aprovada pelo Pedro.
                </p>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="btn-ghost mt-5 text-xs"
                  data-testid="review-form-new"
                >
                  Deixar outra avaliação
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="card p-6 md:p-7 space-y-5" data-testid="review-form">
                <div>
                  <label className="overline block mb-2">A tua classificação *</label>
                  <StarsInput value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
                </div>
                <div>
                  <label className="overline block mb-2">Nome *</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="O teu nome"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    data-testid="review-form-nome"
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Comentário (opcional)</label>
                  <textarea
                    rows={3}
                    className="input w-full resize-none"
                    placeholder="Conta a tua experiência no estúdio..."
                    value={form.comentario}
                    onChange={(e) => setForm({ ...form, comentario: e.target.value })}
                    data-testid="review-form-comentario"
                  />
                </div>

                {error ? (
                  <div className="text-sm text-yellow-300 bg-yellow-400/10 border border-yellow-400/30 px-3 py-2 rounded" data-testid="review-form-error">
                    {error}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid="review-form-submit"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {submitting ? "A publicar..." : "Publicar avaliação"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
